import {
  formatAsCurrency,
  DefaultCreditCard,
  DefaultBankAccountList,
  AccountFundingOption,
  FundingSettlementTime,
  BankAccountConnectionType,
  Box,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import FundingMethodModule from './FundingMethodModule';
import FundingMethodRow from './FundingMethodRow';
import FundingMethodRowSkeleton from './FundingMethodRowSkeleton';
import FundingMethodEmptyState from './FundingMethodEmptyState';

type DefaultFundingMethodsProps = {
  defaultBankAccounts: DefaultBankAccountList[];
  defaultCreditCards: DefaultCreditCard[];
  fundingOptions: AccountFundingOption[];
  isLoading: boolean;
  isCreditCardFundingEnabled: boolean;
  onClickFundBankAccount?: (account: DefaultBankAccountList) => void;
  onClickWithdrawBankAccount?: (account: DefaultBankAccountList) => void;
  onClickFundCreditCard?: (card: DefaultCreditCard) => void;
  onClickContactSupport: () => void;
  onClickBankAccountDetails: (account: DefaultBankAccountList) => void;
  onClickCreditCardDetails: (card: DefaultCreditCard) => void;
};

export default function DefaultFundingMethods({
  defaultBankAccounts,
  defaultCreditCards,
  fundingOptions,
  isLoading,
  isCreditCardFundingEnabled,
  onClickFundBankAccount,
  onClickWithdrawBankAccount,
  onClickFundCreditCard,
  onClickContactSupport,
  onClickBankAccountDetails,
  onClickCreditCardDetails,
}: DefaultFundingMethodsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.OperationalAccounts' });
  const noFeeLabel = t('FundingMethod.noFee', { defaultValue: 'No fee' });

  function getBankAccountNumber(account: DefaultBankAccountList): string {
    return account.ABARoutingNumber
      ? `${account.ABARoutingNumber}-${account.AccountNumber}`
      : `${account.FinancialInstitutionNumber}-${account.BranchTransitNumber}-${account.AccountNumber}`;
  }

  function getConnectionType(accountType: BankAccountConnectionType): string {
    return accountType === 'manual'
      ? t('FundingMethod.connectionManual', { defaultValue: 'Manual' })
      : t('FundingMethod.connectionOnline', { defaultValue: 'Online' });
  }

  function getSettlementSpeed(settlementTime: FundingSettlementTime): string {
    if (settlementTime === 'immediately') {
      return t('FundingMethod.settlementImmediately', { defaultValue: 'Immediately' });
    }

    return t('FundingMethod.settlementBusinessDays', {
      count: Number(settlementTime.slice(1)),
      defaultValue: '{{count}} business days',
    });
  }

  function getFeeAndSettlement(option: AccountFundingOption | undefined) {
    if (!option) return { fee: '', isFeeFree: false, settlementSpeed: '' };

    const feePercent = Number(option.FeePercent || '0');
    const feePerTransaction = Number(option.FeePerTransaction || '0');
    const isFeeFree = feePercent === 0 && feePerTransaction === 0;

    const feeParts = [
      feePerTransaction > 0 ? formatAsCurrency(feePerTransaction.toFixed(2)) : null,
      feePercent > 0 ? `${feePercent}%` : null,
    ].filter(Boolean);

    return {
      fee: isFeeFree ? noFeeLabel : feeParts.join(' + '),
      isFeeFree,
      settlementSpeed: getSettlementSpeed(option.SettlementTime),
    };
  }

  const showBankModule = isLoading || defaultBankAccounts.length > 0;
  const showCreditModule = isLoading || !isCreditCardFundingEnabled || defaultCreditCards.length > 0;

  return (
    <Box gapSize="24px">
      {showBankModule && (
        <FundingMethodModule
          icon="account_balance"
          iconFill={1}
          title={t('FundingMethod.bankAccountsTitle', { defaultValue: 'Bank Accounts' })}
        >
          {isLoading ? (
            <FundingMethodRowSkeleton />
          ) : (
            [...defaultBankAccounts]
              .sort((account1, account2) => account1.Currency.localeCompare(account2.Currency))
              .map((account) => {
                const feeAndSettlement = getFeeAndSettlement(
                  fundingOptions.find((option) => option.Source === 'bank' && option.Currency === account.Currency),
                );

                return (
                  <FundingMethodRow
                    key={account.Token}
                    icon="account_balance"
                    iconFill={1}
                    accountNumber={getBankAccountNumber(account)}
                    currency={account.Currency}
                    onClickAccountDetails={() => onClickBankAccountDetails(account)}
                    fee={feeAndSettlement.fee}
                    isFeeFree={feeAndSettlement.isFeeFree}
                    settlementSpeed={feeAndSettlement.settlementSpeed}
                    connectionType={getConnectionType(account.Type)}
                    onClickFund={onClickFundBankAccount ? () => onClickFundBankAccount(account) : undefined}
                    onClickWithdraw={onClickWithdrawBankAccount ? () => onClickWithdrawBankAccount(account) : undefined}
                  />
                );
              })
          )}
        </FundingMethodModule>
      )}

      {showCreditModule && (
        <FundingMethodModule
          icon="credit_card"
          title={t('FundingMethod.creditCardsTitle', { defaultValue: 'Credit Cards' })}
        >
          {isLoading ? (
            <FundingMethodRowSkeleton />
          ) : isCreditCardFundingEnabled ? (
            defaultCreditCards.map((card) => {
              const feeAndSettlement = getFeeAndSettlement(fundingOptions.find((option) => option.Source === 'credit'));

              return (
                <FundingMethodRow
                  key={card.CreditCardToken}
                  icon="credit_card"
                  accountNumber={card.CreditCardNumber}
                  onClickAccountDetails={() => onClickCreditCardDetails(card)}
                  fee={feeAndSettlement.fee}
                  isFeeFree={feeAndSettlement.isFeeFree}
                  settlementSpeed={feeAndSettlement.settlementSpeed}
                  onClickFund={onClickFundCreditCard ? () => onClickFundCreditCard(card) : undefined}
                />
              );
            })
          ) : (
            <FundingMethodEmptyState
              icon="add_card"
              title={t('FundingMethod.creditCardEmptyTitle', { defaultValue: 'Add Credit Card Funding' })}
              description={t('FundingMethod.creditCardEmptyDescription', {
                defaultValue: 'Contact support to enable this option (fee applies).',
              })}
              actionLabel={t('FundingMethod.contactSupport', { defaultValue: 'Contact Support' })}
              actionIcon="chat_bubble_outline"
              onClickAction={onClickContactSupport}
            />
          )}
        </FundingMethodModule>
      )}
    </Box>
  );
}
